import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * TAKING SOMEBODY TO A #SECTION ON A PAGE THE SERVER DID NOT RENDER.
 *
 * The browser honours a #hash once, while parsing the document. This is a
 * client-rendered app: at that moment `#root` is empty, so there is nothing
 * with that id and the jump silently does nothing. React Router does not
 * redo it. Three pages had each grown their own answer — the same
 * `setTimeout(..., 60)` copied into Landing, Solutions and About — and 60ms
 * is a guess about how long React and a fetch take.
 *
 * On the landing page the guess lost, in two different ways:
 *
 *   · the section existed but was still MOVING. Find Your Next Home sits
 *     directly above #pricing and fills from /discover/featured, so the
 *     target slid down after the scroll. Clicking Pricing landed 233px
 *     short, on the section above — you saw the rentals search and
 *     concluded Pricing was pointing at the wrong thing.
 *   · on a cold load at /#pricing the section did not exist at 60ms at all,
 *     getElementById returned null, and nothing scrolled: the visitor was
 *     left at the top of a 17,000px page with no sign their click did
 *     anything.
 *
 * So: wait for the element rather than guess, then hold the position while
 * the page finishes settling. No timing constant anybody has to tune.
 *
 * WHAT IT WILL NOT DO IS FIGHT THE READER. A wheel, a touch or a key stops
 * it for good. A page that yanks itself back while somebody is reading is
 * worse than a page that never scrolled.
 *
 * It jumps rather than animating, and that is a deliberate change. The first
 * version of this kept the old smooth scroll for what it guessed were
 * in-page clicks — "the element was already there on the first frame, so the
 * page must be settled". That guess is wrong: React renders the whole tree
 * in one pass, so on a COLD load the section is there on the first frame
 * too. The smooth branch therefore ran on exactly the case it was meant to
 * exclude, scrolled, stopped, and let the fetch push the target away again —
 * the original bug, faithfully reproduced. There is no reliable way to tell
 * the two apart from here, so there is no heuristic: always jump, always
 * hold. Across twelve thousand pixels a jump is the kinder one anyway.
 */
export function useHashScroll({ topWhenNoHash = false } = {}) {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      if (topWhenNoHash) window.scrollTo(0, 0);
      return;
    }
    /*
     * A hash is whatever was typed or pasted, and decodeURIComponent throws a
     * URIError on a malformed escape - '#%' is enough. Thrown from an effect
     * that runs on the landing page, that is a blank page for a visitor whose
     * only mistake was a truncated link. Decode when it decodes; otherwise
     * take the hash as written and let getElementById find nothing.
     */
    let id: string;
    try { id = decodeURIComponent(hash.slice(1)); } catch { id = hash.slice(1); }

    let frame = 0;
    let done = false;
    /** Where we left the page, and where the target was when we left it. */
    let placedAt = -1;
    let placedOffset = -1;
    /*
     * Bounded on both ends: how long to wait for the element, and how long
     * to keep correcting once it is found. Neither is a guess about React —
     * they are the point at which we give up rather than spin.
     *
     * Counted in time the page was ON SCREEN, not on the wall clock, and
     * that distinction is load-bearing. requestAnimationFrame does not fire
     * in a hidden tab, so a link opened in a background tab would burn both
     * budgets before a single frame ran, and the reader would switch to it
     * to find the anchor never applied. Capping each step keeps a long spell
     * in the background from counting at all.
     */
    const APPEAR_BUDGET = 4000;
    const SETTLE_BUDGET = 2000;
    let onScreenMs = 0;
    let lastFrame = 0;
    let settleFrom = -1;

    const stop = () => {
      if (done) return;
      done = true;
      cancelAnimationFrame(frame);
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
    };
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('keydown', stop);

    const offsetOf = (el: Element) =>
      Math.round(el.getBoundingClientRect().top + window.scrollY);

    const tick = (now: number) => {
      if (done) return;
      /* A gap longer than a few frames means the tab was hidden or the thread
         was busy; neither is time the reader spent looking at this page. */
      if (lastFrame) onScreenMs += Math.min(now - lastFrame, 100);
      lastFrame = now;

      const el = document.getElementById(id);

      if (!el) {
        /* Not rendered yet. Give up eventually rather than spin for ever on
           a hash that names nothing — a stale bookmark, or a typo. */
        if (onScreenMs > APPEAR_BUDGET) return stop();
        frame = requestAnimationFrame(tick);
        return;
      }

      if (placedAt < 0) {
        el.scrollIntoView({ block: 'start' });
        settleFrom = onScreenMs;
        placedAt = Math.round(window.scrollY);
        placedOffset = offsetOf(el);
      } else if (offsetOf(el) !== placedOffset || Math.round(window.scrollY) !== placedAt) {
        /*
         * Something moved: the section slid down as a fetch rendered above
         * it, or the browser restored a scroll position of its own after we
         * had already jumped. Both are reasons to put it back, and neither
         * is the reader — the reader is detected by their own wheel, touch
         * or key, which stops this outright rather than being inferred from
         * a scroll position.
         */
        el.scrollIntoView({ block: 'start' });
        placedAt = Math.round(window.scrollY);
        placedOffset = offsetOf(el);
      }

      if (onScreenMs - settleFrom > SETTLE_BUDGET) return stop();
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return stop;
  }, [hash, topWhenNoHash]);
}
